/* assets/compare.js — native product compare. SPEC_NATIVE-COMPARE Rev 2 §7.2.
 * Written 2026-09-04b. Amended 2026-09-04d: D8 (slot cap), D9 (empty rows),
 * D10 (thumbnails, Close, right-edge tab, layer order vs John), D10.1 (tab only
 * while closed), D10.2 (button order + trash icon + right-to-left chip list).
 * Amended 2026-09-04f: D11 (tab glyph and rotated label) and the RFQ url.
 * Amended 2026-09-04i: D13 (the RFQ price shape — dollars, from price_raw/100).
 *
 * 🔴 CORRECTED 2026-09-04f — THIS HEADER SAID "Dev theme only" AND HAD BEEN FALSE
 * SINCE 2026-09-04T19:57:18Z, when Pete published `Native Compare Dev 2026-09-04`
 * (theme 182940664092) and it became MAIN. It is LIVE, on every page of the store.
 * ⚠️ Phase 1 is still additive — the Comparable APP remains INSTALLED but its embed
 * is disabled, so its JS never loads. Its hand-written checkbox markup was deleted
 * from `card-product.liquid` on 2026-09-04d because it rendered a dead "Add to
 * compare" beside ours. It has NOT been uninstalled; if it is ever re-enabled, that
 * dead control comes back inside our modal (2026-09-04d D9b).
 *
 * --------
 * THE ONE THING THIS FILE EXISTS TO GET RIGHT
 * The Comparable app binds its checkboxes PER ELEMENT, on the collection-template
 * init pass only, and window.comparableInit() is guarded so re-calling it is a
 * no-op. That is why its checkboxes on homepage Best Sellers, on /search and on
 * related-products have never done anything (HANDOFF-2026-05-28-session-177).
 * ONE DELEGATED LISTENER ON `document` cannot have that bug. Not "is less likely
 * to" — cannot, by construction. Everything else here is downstream of that.
 * --------
 *
 * DECISIONS THIS FILE IMPLEMENTS
 *   D6 → ship WITHOUT highlight-best-value. `lo` IS emitted on six keys and is
 *     deliberately ignored here — kept in the blob for the day it is wanted.
 *   D7 → `rung_order` is READ FROM THE ENVELOPE, shipped by
 *     snippets/ut-rung-order.liquid. 🔴 DO NOT PASTE THE KEY LIST INTO THIS FILE.
 *   D3 → both CTAs. "Request a Quote" reuses the GLOBAL ut-info-modal trigger
 *     contract, so there is no second RFQ form and no duplicate field ids.
 *   D4 → discontinued does NOT mean unbuyable. Add-to-cart stays live there.
 *   D8 → TWO COLUMNS BELOW 750px, FOUR ABOVE. See maxSlots().
 *   D9 → A ROW EMPTY IN EVERY COLUMN DOES NOT RENDER. See table().
 *   D10 → thumbnails, a Close button, and a right-edge tab. John (#john-fab,
 *     z 2147483000) holds the bottom-right and the back-to-top arrow moved to
 *     bottom-centre for him, so compare sits one layer above John.
 *   D10.1 → THE TAB APPEARS ONLY WHEN THE BAR IS CLOSED. See paintTab().
 *   D10.2 (Pete, 2026-09-04d) → BUTTON ORDER IS `Compare · Close · trash`, Clear is
 *     the trashcan, and the chip list builds from the RIGHT. See paint().
 *   D11 (Pete, 2026-09-04f) → THE TAB GLYPH IS NOT SCALES — that is the app we are
 *     leaving. Two crossing arrows, and the label is rotated so the tab is 44px
 *     wide instead of 76. See SHUFFLE_SVG and paintTab().
 */
(function () {
  'use strict';

  /* LOAD-ONCE GUARD. A second <script src> for this file would register a SECOND
   * delegated change listener and every tick would toggle twice — an add
   * immediately undone, which looks exactly like the app's silent no-op. */
  if (window.__utCompareLoaded) return;
  window.__utCompareLoaded = true;

  var STATE_KEY   = 'ut_compare';
  var STATE_V     = 1;                     // bump ⇒ old state discarded silently
  var TTL_MS      = 24 * 60 * 60 * 1000;   // 24h
  var BLOB_MAJOR  = 2;                     // refuse anything else, loudly

  /* 🔑 THE SAME THRESHOLD THE PDP KEY BAND USES (spec_layout / ut-spec-ladder.liquid,
   * ruled 2026-09-09c2): a ladder of 4 rungs or fewer is already scannable and is
   * never worth a click. Above it, the rungs go behind the marquee.
   * ⚠️ ONE DELIBERATE DIVERGENCE FROM THE PDP, and §9 now permits it explicitly:
   * the PDP collapses in the KEY BAND ONLY, so ut505a's 13-rung Insulation
   * Resistance stays expanded there and collapses HERE. The surfaces have
   * different jobs — this one is for scanning across columns. */
  var LADDER_COLLAPSE_OVER = 4;

  /* 🔑 D10 — STATE_V IS DELIBERATELY *NOT* BUMPED FOR THE THUMBNAIL.
   * `items[]` gained an `image` field. A version bump would have been the tidy
   * move and it would have SILENTLY EMPTIED EVERY SHOPPER'S TRAY on deploy —
   * readState() discards on `s.v !== STATE_V`. An old item simply has no `image`
   * and its chip renders without a thumbnail, which is the same fallback a product
   * with no featured_image already needs. Absence is handled, so absence is free. */

  /* -------- D8: the slot cap is a FUNCTION OF THE VIEWPORT, not a constant -------- */
  var MAX_SLOTS_WIDE   = 4;
  var MAX_SLOTS_NARROW = 2;
  var NARROW_MQ        = '(max-width: 749px)';

  function maxSlots() {
    return (window.matchMedia && window.matchMedia(NARROW_MQ).matches)
      ? MAX_SLOTS_NARROW
      : MAX_SLOTS_WIDE;
  }

  function fullMessage() {
    return maxSlots() === MAX_SLOTS_NARROW
      ? 'Compare holds 2 on a phone — remove one to add another.'
      : 'Compare full — remove one to add another.';
  }

  var BAND_LABEL = {
    lifecycle: 'Product Status',
    key:       'Key Specifications',
    details:   'Details',
    features:  'Features',
    general:   'General Information'
  };

  var EM_DASH = '—';
  var CHECK   = '✓';
  var CROSS   = '✗';

  /* The sentinel cellKey() returns for "this cell has nothing". Named because D9
   * tests against it and a bare ' none' literal in two places is a drift engine. */
  var NO_VALUE = ' none';

  /* D10 / D10.2 — the two glyphs. ⚠️ These are the ONLY innerHTML in this file and
   * they are frozen literals with no interpolation of any kind — no handle, no
   * title, no state. Everything that touches product data is built with
   * createElement and textContent, and it must stay that way. */
  /* 🔴 D11 (Pete, 2026-09-04f) — THE SCALES ARE GONE, AND THE REASON IS NOT
   * AESTHETIC. Pete: *"I don't want to use the scales, because that's the app that
   * we're leaving. I don't want them thinking we ripped them off."* A pair of
   * balance scales is the Comparable app's own mark; wearing the departing app's
   * icon on the feature built to replace it is the one visual choice that has no
   * defence, and it costs nothing to avoid.
   *
   * SHUFFLE_SVG is two crossing arrows — bottom-left rises to top-right, top-left
   * falls to bottom-right, arrowheads on the right of each. It reads as "these two,
   * side by side", it is nobody's logo, and it shares no silhouette with scales.
   * ⚠️ SCALES_SVG is DELETED rather than left unreferenced: a dead constant with a
   * name this specific is an invitation for a later session to "restore" it. */
  var SHUFFLE_SVG =
    '<svg viewBox="0 0 24 24" width="100%" height="100%" fill="none" ' +
    'stroke="currentColor" stroke-width="1.7" stroke-linecap="round" ' +
    'stroke-linejoin="round" aria-hidden="true" focusable="false">' +
    '<path d="M3.2 7.2h2.6c1.2 0 2.3.6 3 1.6l4.4 6.4c.7 1 1.8 1.6 3 1.6h3.6"/>' +
    '<path d="M3.2 16.8h2.6c1.2 0 2.3-.6 3-1.6l4.4-6.4c.7-1 1.8-1.6 3-1.6h3.6"/>' +
    '<path d="M17 13.9l2.9 2.9-2.9 2.9M17 4.3l2.9 2.9-2.9 2.9"/></svg>';

  var TRASH_SVG =
    '<svg viewBox="0 0 24 24" width="100%" height="100%" fill="none" ' +
    'stroke="currentColor" stroke-width="1.7" stroke-linecap="round" ' +
    'stroke-linejoin="round" aria-hidden="true" focusable="false">' +
    '<path d="M3.6 6.4h16.8M9.6 6.4V4.6a1.1 1.1 0 0 1 1.1-1.1h2.6a1.1 1.1 0 0 1 1.1 1.1v1.8"/>' +
    '<path d="M5.9 6.4 7 19.5a1.6 1.6 0 0 0 1.6 1.5h6.8a1.6 1.6 0 0 0 1.6-1.5l1.1-13.1"/>' +
    '<path d="M10.2 10.2v6.6M13.8 10.2v6.6"/></svg>';

  // -------- state --------

  function blankState() {
    return { v: STATE_V, cat: null, ts: Date.now(), items: [], closed: false };
  }

  function readState() {
    var raw;
    try { raw = window.localStorage.getItem(STATE_KEY); } catch (e) { return blankState(); }
    if (!raw) return blankState();
    var s;
    try { s = JSON.parse(raw); } catch (e) { return blankState(); }
    if (!s || s.v !== STATE_V) return blankState();          // version bump ⇒ discard
    if (!s.ts || (Date.now() - s.ts) > TTL_MS) return blankState();
    if (!Array.isArray(s.items)) return blankState();
    return s;
  }

  function writeState(s) {
    s.ts = Date.now();
    try { window.localStorage.setItem(STATE_KEY, JSON.stringify(s)); } catch (e) { /* private mode */ }
  }

  function indexOfHandle(s, handle) {
    for (var i = 0; i < s.items.length; i++) if (s.items[i].handle === handle) return i;
    return -1;
  }

  // -------- the delegated listener — the whole point (§7.2) --------

  document.addEventListener('change', function (e) {
    var box = e.target.closest && e.target.closest('.ut-compare__checkbox');
    if (!box) return;
    toggle(box);
  });

  document.addEventListener('click', function (e) {
    var t = e.target.closest ? e.target.closest('[data-ut-compare-action]') : null;
    if (!t) return;
    e.preventDefault();
    var action = t.getAttribute('data-ut-compare-action');
    if (action === 'open')    openModal();
    if (action === 'clear')   clearAll();
    if (action === 'close')   closeModal();      // the MODAL's close
    if (action === 'dismiss') setClosed(true);   // D10 — the BAR's close
    /* D10.1 — the tab is only ON SCREEN when the bar is closed, so its one job is
     * to open. Not a toggle: a toggle would read the CURRENT `closed` value, and a
     * tab that is visible can only mean closed=true anyway. Setting it directly is
     * the same result with no dependence on a value that could have moved. */
    if (action === 'tab')     setClosed(false);
    if (action === 'remove')  removeHandle(t.getAttribute('data-handle'));
  });

  /* D10 — CLOSE IS NOT CLEAR. `closed` hides the bar and KEEPS the tray; `clear`
   * empties it. D10.2 moved Clear to a trashcan and put it LAST, so the two are no
   * longer adjacent and the destructive one is furthest from Compare. */
  function setClosed(v) {
    var s = readState();
    s.closed = !!v;
    writeState(s);
    paint();
  }

  function toggle(box) {
    var handle = box.dataset.handle;
    var cat    = box.dataset.cat;
    var title  = box.dataset.title || handle;
    var s      = readState();
    var at     = indexOfHandle(s, handle);

    if (at !== -1) {                                   // removing
      s.items.splice(at, 1);
      if (!s.items.length) s.cat = null;               // empty set releases the lock
      writeState(s);
      paint();
      return;
    }

    /* THE CATEGORY GATE (§4). Key is blob.cat — the bucket — never product.type
     * and never spec_type. */
    if (s.cat && s.cat !== cat) {
      box.checked = false;
      say('Comparing ' + s.cat + '. Clear to compare a different type.');
      paint();
      return;
    }
    if (s.items.length >= maxSlots()) {               // D8 — read, never cached
      box.checked = false;
      say(fullMessage());
      paint();
      return;
    }

    s.cat = cat;
    s.items.push({
      handle: handle,
      title: title,
      /* D10 — captured AT TICK TIME from the card, because this is the only moment
       * a product's image is in scope. The tray renders from localStorage on other
       * pages where that product's card does not exist. */
      image: box.dataset.image || ''
    });
    /* 🔑 AN ADD ALWAYS RE-OPENS THE BAR. The shopper just acted; showing them the
     * result is the feedback, and a tray that stays hidden after a tick is
     * indistinguishable from a tick that did nothing — the app's defect (§4). */
    s.closed = false;
    writeState(s);
    paint();
  }

  function removeHandle(handle) {
    var s = readState();
    var at = indexOfHandle(s, handle);
    if (at === -1) return;
    s.items.splice(at, 1);
    if (!s.items.length) s.cat = null;
    writeState(s);
    paint();
    if (modalIsOpen()) { s.items.length ? openModal() : closeModal(); }
  }

  function clearAll() {
    writeState(blankState());
    paint();
    closeModal();
  }

  // -------- the bar (D5) and the tab (D10) --------

  function bar() {
    var b = document.getElementById('ut-compare-bar');
    if (b) return b;
    b = document.createElement('div');
    b.id = 'ut-compare-bar';
    b.className = 'ut-compare-bar';
    b.setAttribute('role', 'region');
    b.setAttribute('aria-label', 'Compare tray');
    b.hidden = true;
    document.body.appendChild(b);
    return b;
  }

  function tab() {
    var el = document.getElementById('ut-compare-tab');
    if (el) return el;
    el = document.createElement('button');
    el.type = 'button';
    el.id = 'ut-compare-tab';
    el.className = 'ut-compare-tab ut-compare-tab--away';
    el.setAttribute('data-ut-compare-action', 'tab');
    el.setAttribute('aria-controls', 'ut-compare-bar');
    document.body.appendChild(el);
    return el;
  }

  /* D10.1 (Pete, 2026-09-04d) — THE TAB IS A REOPENER, NOT A SECOND TOGGLE.
   * *"We don't need the side tab while the bottom is open. it only appears when the
   * bottom is closed."*
   *
   * SHOWN when: the tray has items AND the bar is closed.
   * HIDDEN when: the tray is empty (nothing to reopen), OR the bar is already open
   *              (it carries its own Close, so the tab would be a duplicate control
   *              for one piece of state — and on the right edge it also sat over the
   *              product grid for no reason).
   *
   * ⚠️ It slides away rather than display:none, so it animates back in when the bar
   * is closed. `--away` also sets pointer-events:none so an off-screen tab cannot
   * swallow a click on the content behind it. */
  function paintTab(s) {
    var el   = tab();
    var n    = s.items.length;
    var show = n > 0 && !!s.closed;

    if (!show) {
      el.classList.add('ut-compare-tab--away');
      el.setAttribute('aria-hidden', 'true');
      el.tabIndex = -1;
      return;
    }

    el.textContent = '';

    var badge = document.createElement('span');
    badge.className = 'ut-compare-tab__count';
    badge.textContent = String(n);

    var icon = document.createElement('span');
    icon.className = 'ut-compare-tab__icon';
    icon.innerHTML = SHUFFLE_SVG;         // frozen literal — see SHUFFLE_SVG

    /* D11 — the class is what rotates it. The CSS puts `writing-mode: vertical-rl`
     * on THIS span and never on the button, because the button is a flex column and
     * writing-mode redefines what its column axis is. */
    var word = document.createElement('span');
    word.className = 'ut-compare-tab__word';
    word.textContent = 'Compare';

    /* D11 — ORDER IS badge · word · icon, top to bottom. The icon moved BELOW the
     * label to match the reference tabs Pete sent, and because the badge and the
     * glyph are both circles-ish: stacked adjacent they read as one smudge at
     * arm's length, and the word between them separates them. */
    el.appendChild(badge);
    el.appendChild(word);
    el.appendChild(icon);

    el.classList.remove('ut-compare-tab--away');
    el.removeAttribute('aria-hidden');
    el.tabIndex = 0;
    /* Always "false": the tab is only ever on screen while the bar is collapsed. */
    el.setAttribute('aria-expanded', 'false');
    el.setAttribute('aria-label',
      'Open compare tray, ' + n + (n === 1 ? ' product' : ' products'));
  }

  function paint() {
    var s = readState();
    var b = bar();

    paintTab(s);

    if (!s.items.length) {
      b.hidden = true;
      b.textContent = '';
      b.classList.add('ut-compare-bar--closed');
      syncBoxes(s);
      return;
    }

    b.textContent = '';

    /* D10.2 — the chips pack to the RIGHT end of this box (CSS
     * `justify-content:flex-end`), so the newest sits beside the controls the
     * shopper is reaching for and the list grows leftward. Pete: *"build the list
     * from the right to the left. we put ours all the way out in left field."* */
    var list = document.createElement('div');
    list.className = 'ut-compare-bar__items';
    s.items.forEach(function (it) {
      var chip = document.createElement('span');
      chip.className = 'ut-compare-bar__chip';

      /* D10 — §7.2 D5 asked for thumbnails on 2026-07-27 and the 2026-09-04b build
       * shipped text pills. It was not a shortcut: the tray renders from
       * localStorage, which held only {handle, title}, so there was no image to
       * draw. The fix was in the SNIPPET (data-image), not here. */
      if (it.image) {
        var im = document.createElement('img');
        im.className = 'ut-compare-bar__chip-img';
        im.src = it.image;
        im.alt = '';
        im.loading = 'lazy';
        chip.appendChild(im);
      }

      var name = document.createElement('span');
      name.className = 'ut-compare-bar__chip-title';
      name.textContent = it.title;

      var x = document.createElement('button');
      x.type = 'button';
      x.className = 'ut-compare-bar__chip-remove';
      x.setAttribute('data-ut-compare-action', 'remove');
      x.setAttribute('data-handle', it.handle);
      x.setAttribute('aria-label', 'Remove ' + it.title + ' from compare');
      x.textContent = '×';

      chip.appendChild(name);
      chip.appendChild(x);
      list.appendChild(chip);
    });

    /* D8 — the denominator is the CURRENT cap, so the tray reads 3/4 on a laptop
     * and 3/2 on a phone holding the same state. 3/2 looks wrong and IS right. */
    var count = document.createElement('span');
    count.className = 'ut-compare-bar__count';
    count.textContent = s.items.length + '/' + maxSlots();

    var open = document.createElement('button');
    open.type = 'button';
    open.className = 'button ut-compare-bar__open';
    open.setAttribute('data-ut-compare-action', 'open');
    open.textContent = 'Compare';
    open.disabled = s.items.length < 2;

    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'ut-compare-bar__close';
    close.setAttribute('data-ut-compare-action', 'dismiss');
    close.textContent = 'Close';
    close.setAttribute('aria-label', 'Hide the compare tray, keeping the products');

    /* 🔴 D10.2 — CLEAR IS THE TRASHCAN AND IT IS LAST. Pete, 2026-09-04d: *"I think
     * we need to swap Clear and close. I like the trashcan for it."*
     * ⚠️ This REVERSES the previous note here — *"both are labelled words, never
     * icons, because a shopper who has picked four instruments must not lose them
     * to a mis-read glyph."* That risk is real and is now paid for differently:
     * the destructive control is LAST (never adjacent to Compare), it carries a
     * `title` AND an `aria-label` naming the count it discards, and CSS gives it a
     * 40x40 target. ▶️ If a shopper ever reports losing a tray, add a confirm — do
     * NOT revert to a word, which is the mitigation Pete replaced. */
    var n = s.items.length;
    var clearLabel = 'Clear compare — removes all ' + n +
                     (n === 1 ? ' product' : ' products');
    var clear = document.createElement('button');
    clear.type = 'button';
    clear.className = 'ut-compare-bar__clear';
    clear.setAttribute('data-ut-compare-action', 'clear');
    clear.setAttribute('aria-label', clearLabel);
    clear.title = clearLabel;
    var clearIcon = document.createElement('span');
    clearIcon.className = 'ut-compare-bar__clear-icon';
    clearIcon.innerHTML = TRASH_SVG;      // frozen literal — see TRASH_SVG
    clear.appendChild(clearIcon);

    /* ORDER IS THE DECISION, not an accident of append sequence: D10.2 puts
     * Compare first (the primary), Close second, and the destructive trash last. */
    b.appendChild(list);
    b.appendChild(count);
    b.appendChild(open);
    b.appendChild(close);
    b.appendChild(clear);

    b.hidden = false;
    b.classList.toggle('ut-compare-bar--closed', !!s.closed);

    syncBoxes(s);
  }

  /* Checkbox state is DERIVED FROM STATE, never the other way round. */
  function syncBoxes(s) {
    s = s || readState();
    var cap   = maxSlots();                            // D8 — once per pass, current
    var boxes = document.querySelectorAll('.ut-compare__checkbox');
    for (var i = 0; i < boxes.length; i++) {
      var box = boxes[i];
      var on  = indexOfHandle(s, box.dataset.handle) !== -1;
      if (box.checked !== on) box.checked = on;
      var blocked = !!(s.cat && s.cat !== box.dataset.cat) || (!on && s.items.length >= cap);
      box.disabled = blocked;
      var slot = box.closest('.ut-compare');
      if (slot) {
        slot.classList.toggle('ut-compare--blocked', blocked);
        /* NEVER A SILENT NO-OP — that is precisely the app's defect (§4). */
        slot.title = blocked
          ? (s.cat && s.cat !== box.dataset.cat
              ? 'Comparing ' + s.cat + '. Clear to compare a different type.'
              : fullMessage())
          : '';
      }
    }
  }

  function say(msg) {
    var n = document.getElementById('ut-compare-toast');
    if (!n) {
      n = document.createElement('div');
      n.id = 'ut-compare-toast';
      n.className = 'ut-compare-toast';
      n.setAttribute('role', 'status');
      document.body.appendChild(n);
    }
    n.textContent = msg;
    n.classList.add('is-on');
    window.clearTimeout(n._t);
    n._t = window.setTimeout(function () { n.classList.remove('is-on'); }, 4000);
  }

  // -------- fetch --------

  /* 🔴 `Accept: text/html` IS LOAD-BEARING AND MUST NOT BE "IMPROVED" TO
   * application/json. Shopify's /products/<handle> route serves its OWN built-in
   * product JSON whenever the request prefers application/json, and it IGNORES
   * `?view=` when it does. Measured live 2026-09-04b on ut505a:
   *   no Accept header      -> 6,337 bytes, our envelope, ok:true
   *   Accept: text/html     -> 6,337 bytes, our envelope, ok:true
   *   Accept: application/json, text/html -> 12,542 bytes, {"product":{...}}
   * The first build sent the third one and every column was refused as "no-data". */
  function fetchOne(handle) {
    return window.fetch('/products/' + encodeURIComponent(handle) + '?view=compare-json', {
      credentials: 'same-origin',
      headers: { 'Accept': 'text/html' }
    })
      .then(function (r) { return r.text(); })
      .then(function (txt) {
        try { return JSON.parse(txt); }
        catch (e) {
          return { ok: false, handle: handle, reason: 'unparseable',
                   _raw: txt.slice(0, 200) };
        }
      })
      .catch(function () { return { ok: false, handle: handle, reason: 'fetch-failed' }; });
  }

  // -------- the modal --------

  function modalIsOpen() {
    var m = document.getElementById('ut-compare-modal');
    return !!m && !m.hidden;
  }

  function closeModal() {
    var m = document.getElementById('ut-compare-modal');
    if (m) { m.hidden = true; m.textContent = ''; }
    document.documentElement.classList.remove('ut-compare-locked');
  }

  function modalShell() {
    var m = document.getElementById('ut-compare-modal');
    if (!m) {
      m = document.createElement('div');
      m.id = 'ut-compare-modal';
      m.className = 'ut-compare-modal';
      m.setAttribute('role', 'dialog');
      m.setAttribute('aria-modal', 'true');
      m.setAttribute('aria-label', 'Product comparison');
      document.body.appendChild(m);
    }
    return m;
  }

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && modalIsOpen()) closeModal();
  });

  function openModal() {
    var s = readState();
    if (s.items.length < 2) { say('Add at least two products to compare.'); return; }

    var m = modalShell();
    m.textContent = '';
    m.hidden = false;
    document.documentElement.classList.add('ut-compare-locked');

    var loading = document.createElement('p');
    loading.className = 'ut-compare-modal__loading';
    loading.textContent = 'Loading comparison…';
    m.appendChild(loading);

    Promise.all(s.items.map(function (it) { return fetchOne(it.handle); }))
      .then(function (results) { render(m, results); });
  }

  function render(m, results) {
    m.textContent = '';

    var panel = document.createElement('div');
    panel.className = 'ut-compare-modal__panel';

    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'ut-compare-modal__close';
    close.setAttribute('data-ut-compare-action', 'close');
    close.setAttribute('aria-label', 'Close comparison');
    close.textContent = '×';
    panel.appendChild(close);

    /* -------- Column admission, and the four ways a column is refused --------
     * Each refusal is REPORTED, never silently dropped. A column that vanishes
     * without explanation is indistinguishable from one that was never added. */
    var cols = [], refused = [];
    results.forEach(function (r) {
      if (!r || r.ok !== true) {
        refused.push({ handle: (r && r.handle) || '?', why: (r && r.reason) || 'no-data' });
        return;
      }
      if (!r.data || r.data.v !== BLOB_MAJOR) {
        var got = r.data ? r.data.v : 'none';
        window.console && console.error(
          '[ut-compare] refusing ' + r.handle + ': specs_json v=' + got +
          ', this renderer speaks v=' + BLOB_MAJOR + ' only.');
        refused.push({ handle: r.handle, why: 'schema v' + got });
        return;
      }
      cols.push(r);
    });

    /* D8 — THE FOURTH REFUSAL: TOO NARROW A SCREEN FOR THIS MANY COLUMNS.
     * 🔑 CANNOT BE ENFORCED AT ADD TIME ALONE — state lives in localStorage on ONE
     * browser: tick four on a laptop, open the same browser at phone width, and
     * four columns arrive at a surface measured to need 1.28 screens of scrolling
     * before the first spec row. The overflow goes through the refusal line that
     * already exists, and the tray still holds them for a wider window. */
    var cap = maxSlots();
    if (cols.length > cap) {
      cols.slice(cap).forEach(function (c) {
        refused.push({ handle: c.handle, why: 'not shown at this width' });
      });
      cols = cols.slice(0, cap);
    }

    if (refused.length) {
      var note = document.createElement('p');
      note.className = 'ut-compare-modal__refused';
      note.textContent = 'Not shown: ' + refused.map(function (x) {
        return x.handle + ' (' + x.why + ')';
      }).join(', ');
      panel.appendChild(note);
    }

    if (cols.length < 2) {
      var only = document.createElement('p');
      only.className = 'ut-compare-modal__empty';
      only.textContent = 'Nothing left to compare — at least two products need spec data.';
      panel.appendChild(only);
      m.appendChild(panel);
      return;
    }

    /* rung_order comes off the ENVELOPE (D7), never from a list in this file. */
    var rungOrder = Array.isArray(cols[0].rung_order) ? cols[0].rung_order : [];
    if (!rungOrder.length) {
      window.console && console.warn('[ut-compare] no rung_order in envelope; ' +
        'conditions will render in sorted order. Check snippets/ut-rung-order.liquid.');
    }

    panel.appendChild(header(cols));
    panel.appendChild(diffToggle());
    panel.appendChild(table(cols, rungOrder));
    m.appendChild(panel);
  }

  // -------- the product header row --------

  function header(cols) {
    var wrap = document.createElement('div');
    wrap.className = 'ut-compare-cards';

    cols.forEach(function (c) {
      var card = document.createElement('div');
      card.className = 'ut-compare-card';

      var x = document.createElement('button');
      x.type = 'button';
      x.className = 'ut-compare-card__remove';
      x.setAttribute('data-ut-compare-action', 'remove');
      x.setAttribute('data-handle', c.handle);
      x.setAttribute('aria-label', 'Remove ' + c.title);
      x.textContent = '×';
      card.appendChild(x);

      if (c.image) {
        var img = document.createElement('img');
        img.className = 'ut-compare-card__img';
        img.src = c.image;
        img.alt = '';
        img.loading = 'lazy';
        card.appendChild(img);
      }

      var a = document.createElement('a');
      a.className = 'ut-compare-card__title';
      a.href = c.url;
      a.textContent = c.title;
      card.appendChild(a);

      /* THE LIFECYCLE PILL. RULING_LIVE-TABLES-ARE-FROZEN…_2026-08-19: discontinued
       * models get tables SO BUYERS CAN COMPARE THE OLD TO THE NEW. */
      if (c.replaced_by) {
        var pill = document.createElement('span');
        pill.className = 'ut-compare-card__pill';
        pill.textContent = 'Replaced by ' + c.replaced_by;
        card.appendChild(pill);
      }

      var price = document.createElement('div');
      price.className = 'ut-compare-card__price';
      var now = document.createElement('span');
      now.className = 'ut-compare-card__price-now';
      now.textContent = c.price || '';
      price.appendChild(now);
      /* Decide on the RAW cents, never the formatted string: `nil | money` renders
       * as an empty string, so the string cannot distinguish "no compare-at price"
       * from "one that formatted to nothing". */
      if (c.compare_at_raw && c.price_raw && c.compare_at_raw > c.price_raw) {
        var was = document.createElement('s');
        was.className = 'ut-compare-card__price-was';
        was.textContent = c.compare_at;
        price.appendChild(was);
      }
      card.appendChild(price);

      var cta = document.createElement('div');
      cta.className = 'ut-compare-card__cta';

      /* D4: discontinued does NOT mean unbuyable. */
      var add = document.createElement('button');
      add.type = 'button';
      add.className = 'button button--primary ut-compare-card__add';
      add.textContent = c.available ? 'Add to cart' : 'Out of stock';
      add.disabled = !c.available || !c.variant_id;
      add.addEventListener('click', function () {
        add.disabled = true;
        add.textContent = 'Adding…';
        window.fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: c.variant_id, quantity: 1 }] })
        }).then(function (r) {
          add.textContent = r.ok ? 'Added' : 'Try again';
          add.disabled = !r.ok;
        }).catch(function () { add.textContent = 'Try again'; add.disabled = false; });
      });
      cta.appendChild(add);

      /* D3 — Request a Quote. 🔑 NO SECOND FORM. A TRIGGER for the global
       * ut-info-modal, which binds via its own idempotent MutationObserver, so a
       * button created here binds itself and there are no field ids to collide.
       *
       * 🟢 D11 (2026-09-04f) — THE URL HALF OF 2026-09-04d's OWED ITEM IS CLOSED.
       * This sent `/products/upo1082hd` where the PDP's own `card__quote-link`
       * sends `https://uni-trendus.com/products/utg4104x`. Both land in the same
       * hidden field and go to Make → Brevo + `rfq_requests`, so a quote raised
       * from the compare modal carried a link that is not clickable in an email.
       * ⚠️ `new URL(x, origin)` is used rather than string concatenation because it
       * is a NO-OP on a value that is already absolute — so if the envelope ever
       * starts emitting a full URL this does not produce `https://…https://…`.
       *
       * 🟢 D13 (2026-09-04i) — THE PRICE HALF IS NOW CLOSED, AND THE MAPPING WAS READ
       * BEFORE THE EDIT, NOT AFTER. `-09-04f` left this owed and described the PDP
       * as sending "raw cents (1999.0)". ⚠️ THAT DESCRIPTION WAS WRONG AND IT POINTS
       * THE FIX AT THE WRONG VALUE. Measured on live 2026-09-04i:
       *
       *   PDP trigger  data-product-price   "407.2"   dollars, plain decimal
       *   this button  data-product-price   "$407.20" FORMATTED — the defect
       *   compare-json price_raw            40720     CENTS, integer
       *
       * `1999.0` was simply a $1,999.00 product's DOLLAR price. Nothing on the PDP
       * path is in cents. 🔑 THE ONE FIELD THAT IS IN CENTS IS `price_raw` — the
       * field whose NAME makes it the obvious one-token fix. `c.price` -> `c.price_raw`
       * reads as correct, matches the naming, and ships a silent 100x error.
       *
       * The mapping, read at the source (Make team 2376912, scenario 5462129, the
       * CreateJSON data structure 433225): `line_items[].price` is declared
       * `type: "number"`, `strict: false` — so a formatted string does not raise, it
       * coerces, silently. All 42 `line_items[].price` values ever stored in
       * `rfq_requests` (2026-06-25 -> 2026-09-01) are JSON NUMBERS in DOLLARS; zero
       * strings, zero `$`. And `ut-info-modal` passes this attribute through with NO
       * parse at all — `price: price` straight into the payload.
       *
       * ▶️ Ruled by Pete 2026-09-04i: DOLLARS. Only THIS end changes; the PDP end was
       * already correct, so "change both ends together" was predicated on the wrong
       * reading. `String()` is explicit because setAttribute stringifies anyway and
       * the reader should not have to know that. */
      var rfq = document.createElement('button');
      rfq.type = 'button';
      rfq.className = 'button ut-compare-card__rfq';
      rfq.setAttribute('data-avail-modal-open', '');
      rfq.setAttribute('data-modal-mode', 'quote');
      rfq.setAttribute('data-product-title', c.title);
      rfq.setAttribute('data-product-sku', (c.data && c.data.model) || c.handle);
      rfq.setAttribute('data-product-url',
        c.url ? new URL(c.url, window.location.origin).href : '');
      rfq.setAttribute('data-product-price',
        (c.price_raw == null || c.price_raw === '') ? '' : String(c.price_raw / 100));
      rfq.textContent = 'Request a Quote';
      cta.appendChild(rfq);

      card.appendChild(cta);
      wrap.appendChild(card);
    });

    return wrap;
  }

  /* D14 (Pete, 2026-09-06d) — HIGHLIGHT THE DIFFERENCES, DO NOT SUBTRACT THE REST.
   * The sameness verdict is unchanged: table() still marks every row `.is-same`
   * from cellKey(). Only the VERB moved — the stylesheet now emphasises the rows
   * that differ instead of hiding the ones that do not, so no data leaves the
   * table. ⚠️ `syncBands()` went with it: nothing hides a row any more, so a band
   * can no longer be emptied at toggle time, and a function that cannot change
   * anything is the `NO_HIGHLIGHT` shape — it reads like working code and never
   * runs (L-2026-09-03h-2). D9's BUILD-TIME drop is untouched; it lives in
   * table() pass 1 and never needed syncBands.
   * 🔑 DEFAULT ON. §10(a)'s own finding, applied one level up: behaviour parked
   * behind a control a shopper has to find is behaviour most shoppers never see,
   * and the default view is what they see first. table() sets the class at build
   * time; this box is the way OUT of the highlight, not the way in. */
  function diffToggle() {
    var wrap = document.createElement('label');
    wrap.className = 'ut-compare-diff';
    var cb = document.createElement('input');
    cb.type = 'checkbox';
    cb.className = 'ut-compare-diff__input';
    cb.checked = true;
    cb.addEventListener('change', function () {
      var t = document.querySelector('.ut-compare-table');
      if (!t) return;
      t.classList.toggle('is-diff-highlight', cb.checked);
    });
    var txt = document.createElement('span');
    txt.textContent = 'Highlight differences';
    wrap.appendChild(cb);
    wrap.appendChild(txt);
    return wrap;
  }

  // -------- the table --------

  /* ROW ORDER COMES FROM THE FIRST COLUMN AND IS NEVER RE-SORTED (§2). The category
   * gate guarantees one bucket, and one bucket means one row sequence.
   * ⚠️ A key in a later column and absent from the first is a DEFECT in the emitter.
   * We append it and shout, because dropping it would hide the defect behind a
   * correct-looking table. */
  function rowPlan(cols) {
    var plan = [], seen = {};
    cols[0].data.rows.forEach(function (r) { plan.push(r); seen[r.k] = true; });
    for (var i = 1; i < cols.length; i++) {
      cols[i].data.rows.forEach(function (r) {
        if (seen[r.k]) return;
        seen[r.k] = true;
        plan.push(r);
        window.console && console.warn('[ut-compare] row `' + r.k + '` is in ' +
          cols[i].handle + ' but not in ' + cols[0].handle +
          ' — same bucket, different layout. Appended at the end.');
      });
    }
    return plan;
  }

  function rowFor(col, k) {
    var rows = col.data.rows;
    for (var i = 0; i < rows.length; i++) if (rows[i].k === k) return rows[i];
    return null;
  }

  /* `skipKey` is the condition a GROUP HEADING one row above already states, so the
   * rung label does not repeat it. 🔴 BACKWARD COMPATIBLE ON PURPOSE: cellKey()
   * calls this with two arguments, `skipKey` arrives `undefined`, and `k !== undefined`
   * is true for every real key — so the sameness verdict is byte-identical to before
   * this patch. That matters: cellKey() decides `.is-same`, and a highlight that
   * shifted because of a LAYOUT change would be a defect wearing a feature's clothes.
   * ⚠️ THE NO-VANISH FALLBACK IS THE LAST LINE. If hoisting the group key empties the
   * label, put it back — an empty cell under a heading is the 2026-08-13 rung-label
   * defect returning by a different door. */
  function rungLabel(rung, rungOrder, skipKey) {
    var c = rung.c || {};
    var parts = [];
    rungOrder.forEach(function (k) { if (k !== skipKey && c[k]) parts.push(String(c[k])); });
    Object.keys(c).sort().forEach(function (k) {
      if (k !== skipKey && rungOrder.indexOf(k) === -1 && c[k]) parts.push(String(c[k]));
    });
    if (!parts.length && skipKey && c[skipKey]) parts.push(String(c[skipKey]));
    return parts.join(' · ');
  }

  /* ---- THE GROUPING RULE — a port of snippets/ut-spec-ladder.liquid ----
   * GROUP KEY = the condition present in EVERY rung yielding the FEWEST distinct
   * values, provided that count is >1 and < the rung count. Both guards matter:
   * n = 1 is one heading over everything (theatre); n = rung count is a heading per
   * row (noise). MEASURED over all 780 live ladders: the rule groups 175 and leaves
   * 605 exactly as they were — and WITHOUT the second guard it would fire on `range`
   * or `quantity` and double the height of 554 of them.
   * ⚠️ TWO COPIES NOW EXIST — this one and the Liquid one — because JS cannot import
   * Liquid, the same accepted duplication as `format_rung()`. Change one, change the
   * other. `rung_order` itself is NOT duplicated: it still arrives on the envelope.
   * 🔵 `'#' + v` keys the seen-map so a condition value of `constructor` or
   * `__proto__` cannot collide with Object.prototype. */
  function ladderGroupKey(sub, rungOrder) {
    var first = sub[0].c || {};
    var best = '', bestN = 999;
    for (var i = 0; i < rungOrder.length; i++) {
      var k = rungOrder[i];
      if (!first[k]) continue;
      var present = true, seen = {}, n = 0;
      for (var j = 0; j < sub.length; j++) {
        var cv = (sub[j].c || {})[k];
        if (!cv) { present = false; break; }
        if (seen['#' + cv] !== 1) { seen['#' + cv] = 1; n++; }
      }
      if (!present) continue;
      if (n <= 1) continue;
      if (n >= sub.length) continue;
      if (n < bestN) { bestN = n; best = k; }
    }
    return best;
  }

  /* Distinct group values in FIRST-APPEARANCE order. 🔴 NEVER SORTED — array order is
   * spec_layout's decision and this surface does not get to have an opinion about it.
   * It is also why the render is two-pass: 19 of the 175 grouped ladders arrive with
   * their rungs interleaved, and run-length grouping would give ut505a ten groups
   * where five are correct. */
  function ladderGroupValues(sub, key) {
    var out = [], seen = {};
    for (var i = 0; i < sub.length; i++) {
      var v = (sub[i].c || {})[key];
      if (seen['#' + v] !== 1) { seen['#' + v] = 1; out.push(v); }
    }
    return out;
  }

  /* The shape line: how much is behind the click and how it is cut. NEVER a value —
   * where no marquee exists this is the whole summary, and it must assert nothing
   * about performance that has no source. */
  function ladderShape(sub, rungOrder) {
    var key = ladderGroupKey(sub, rungOrder);
    var s = sub.length + ' values';
    if (!key) return s;
    var gv = ladderGroupValues(sub, key);
    return s + ' · ' + (gv.length <= 3 ? gv.join(' / ') : gv.length + ' groups');
  }

  /* 🔴 THE SHAPE TEST COMES FIRST, AND `sub` COMES BEFORE `v` (§2, §11).
   * A row carrying `sub` has had its `v` AND `b` DELETED by build_blob().
   * 🔴 AND `b` IS NOT ENOUGH ON ITS OWN — build_blob() sets b:1 on a boolean-typed
   * row EVEN WHEN THE PRODUCT HAS NO VALUE (spec_layout.py:2542). Its docstring
   * rules the render: no "v" ⇒ PDP skips it, compare shows "—". A cross would
   * assert the product LACKS the feature, a claim the data does not make. */
  function cellNode(row, rungOrder) {
    var td = document.createElement('td');

    if (row && row.sub && row.sub.length) {
      var groupKey = ladderGroupKey(row.sub, rungOrder);

      var lad = document.createElement('table');
      lad.className = 'ut-compare-ladder' +
                      (groupKey ? ' ut-compare-ladder--grouped' : '');

      var rungRow = function (rung, skipKey) {
        var tr = document.createElement('tr');
        var th = document.createElement('th');
        th.setAttribute('scope', 'row');
        th.textContent = rungLabel(rung, rungOrder, skipKey);
        var v = document.createElement('td');
        v.textContent = rung.v == null ? EM_DASH : String(rung.v);
        tr.appendChild(th);
        tr.appendChild(v);
        return tr;
      };

      if (!groupKey) {
        row.sub.forEach(function (rung) { lad.appendChild(rungRow(rung, '')); });
      } else {
        ladderGroupValues(row.sub, groupKey).forEach(function (gv) {
          var gtr = document.createElement('tr');
          gtr.className = 'ut-compare-ladder__group';
          var gth = document.createElement('th');
          gth.setAttribute('scope', 'colgroup');
          gth.setAttribute('colspan', '2');
          gth.textContent = gv;
          gtr.appendChild(gth);
          lad.appendChild(gtr);
          row.sub.forEach(function (rung) {
            if ((rung.c || {})[groupKey] === gv) lad.appendChild(rungRow(rung, groupKey));
          });
        });
      }

      /* 🔑 `m` IS THE MARQUEE — the UNCONDITIONED assertion, imported 2026-09-09c.
       * ⚠️ IT IS NOT DERIVABLE FROM THE RUNGS AND MUST NEVER BE COMPUTED FROM THEM.
       * uts3015t+'s best rung is `-157 dBm`; its marquee is `-161dBm/Hz`. A renderer
       * that took the "best" rung would understate the product by 4 dB and would look
       * entirely reasonable doing it. */
      var marquee = (row.m == null || row.m === '') ? '' : String(row.m);

      if (row.sub.length > LADDER_COLLAPSE_OVER) {
        var det = document.createElement('details');
        det.className = 'ut-compare-ladder-more';
        var sum = document.createElement('summary');
        if (marquee) {
          var mq = document.createElement('span');
          mq.className = 'ut-compare-marquee';
          mq.textContent = marquee;
          sum.appendChild(mq);
          var cnt = document.createElement('span');
          cnt.className = 'ut-compare-ladder-count';
          cnt.textContent = ' · ' + ladderShape(row.sub, rungOrder);
          sum.appendChild(cnt);
        } else {
          var only = document.createElement('span');
          only.className = 'ut-compare-ladder-count ut-compare-ladder-count--only';
          only.textContent = ladderShape(row.sub, rungOrder);
          sum.appendChild(only);
        }
        det.appendChild(sum);
        det.appendChild(lad);
        td.appendChild(det);
      } else {
        /* Short ladder: no click. The marquee still leads, because a column that
         * shows a number is the point of this surface. */
        if (marquee) {
          var line = document.createElement('div');
          line.className = 'ut-compare-marquee ut-compare-marquee--above';
          line.textContent = marquee;
          td.appendChild(line);
        }
        td.appendChild(lad);
      }
      return td;
    }

    if (row && row.b === 1 && row.v != null && row.v !== '') {
      var mark = document.createElement('span');
      var yes = (row.v === 'true');
      mark.className = yes ? 'ut-compare-yes' : 'ut-compare-no';
      mark.setAttribute('aria-label', yes ? 'Yes' : 'No');
      mark.textContent = yes ? CHECK : CROSS;
      td.appendChild(mark);
      return td;
    }

    if (row && row.v != null && row.v !== '') {
      td.textContent = String(row.v);
      return td;
    }

    /* THE EM-DASH IS THE INFORMATION ON THIS SURFACE — WHEN SOME OTHER COLUMN HAS
     * A VALUE. ⚠️ When EVERY column is empty there is no comparison to make and the
     * row never reaches this function — see table(), D9. */
    td.textContent = EM_DASH;
    td.className = 'ut-compare-empty';
    return td;
  }

  /* Equality for the differences HIGHLIGHT. ⚠️ Two ladder rows are equal only if their
   * rung SETS are equal — comparing row.v would compare two absent values and call
   * every ladder row identical. */
  function cellKey(row, rungOrder) {
    if (!row) return NO_VALUE;
    if (row.sub && row.sub.length) {
      /* 🔑 THE MARQUEE JOINS THE VERDICT. Two columns with identical rungs but
       * different headline figures are NOT the same cell — and after the collapse the
       * marquee may be the only thing a shopper can see without clicking, so a row
       * that differs only there must still be tinted. ⚠️ `rungLabel` is called with
       * TWO arguments here on purpose: no skipKey, so this key is unchanged for every
       * ladder that has no marquee, and no row silently changes its verdict. */
      var mk = (row.m == null || row.m === '') ? '' : 'M:' + row.m + '|';
      return 'L:' + mk + row.sub.map(function (r) {
        return rungLabel(r, rungOrder) + '=' + (r.v == null ? '' : r.v);
      }).join('|');
    }
    if (row.v == null || row.v === '') return NO_VALUE;
    return (row.b === 1 ? 'B:' : 'V:') + String(row.v);
  }

  function table(cols, rungOrder) {
    var scroller = document.createElement('div');
    scroller.className = 'ut-compare-scroll';

    var t = document.createElement('table');
    t.className = 'ut-compare-table is-diff-highlight';

    var thead = document.createElement('thead');
    var htr = document.createElement('tr');
    var corner = document.createElement('th');
    corner.setAttribute('scope', 'col');
    corner.className = 'ut-compare-table__corner';
    corner.textContent = '';
    htr.appendChild(corner);
    cols.forEach(function (c) {
      var th = document.createElement('th');
      th.setAttribute('scope', 'col');
      th.textContent = (c.data && c.data.model) || c.title;
      htr.appendChild(th);
    });
    thead.appendChild(htr);
    t.appendChild(thead);

    var tbody = document.createElement('tbody');
    var band = null;

    /* ---- D9 PASS 1: decide what survives, using the RENDERER'S OWN PREDICATE ----
     * 🔴 A ROW EMPTY IN EVERY COLUMN IS NOT A COMPARISON, IT IS A BLANK LINE.
     * MEASURED 2026-09-04d on MSO3034X / MSO3054X / MSO2304X: 9 of 46 rows — 20% —
     * were an em-dash in every column, and two of the nine were the WHOLE lifecycle
     * band, which is why the table opened on a heading with two empty rows.
     * 🔑 THIS NARROWS §7.2's EM-DASH RULE TO WHERE IT IS TRUE. A blank in ONE column
     * beside a value in another IS the comparison — untouched.
     * ⚠️ NOT THE DIFFERENCES HIGHLIGHT, which marks rows where the columns are
     * EQUAL and leaves them on screen. This drops only rows where every column is
     * ABSENT — a blank line is not a comparison, so nothing is lost by removing it.
     * 🔑 THE TEST REUSES cellKey(), the function the toggle already decides with. A
     * second hand-written emptiness test would disagree the first time the ladder
     * shape or the boolean rule moved. Import the shipped predicate. */
    var visible = [];
    rowPlan(cols).forEach(function (proto) {
      var keys = cols.map(function (c) { return cellKey(rowFor(c, proto.k), rungOrder); });
      var emptyEverywhere = keys.every(function (k) { return k === NO_VALUE; });
      if (emptyEverywhere) return;
      visible.push({ proto: proto, keys: keys });
    });

    /* ---- PASS 2: render, emitting a band heading only when a SURVIVING row needs
     * one. One pass is what produced the orphaned "Product Status" heading: the
     * heading is written when the band CHANGES, whether or not anything follows. */
    visible.forEach(function (item) {
      var proto = item.proto;

      if (proto.g !== band) {
        band = proto.g;
        var btr = document.createElement('tr');
        btr.className = 'ut-compare-table__band';
        var bth = document.createElement('th');
        bth.setAttribute('scope', 'colgroup');
        bth.setAttribute('colspan', String(cols.length + 1));
        bth.textContent = BAND_LABEL[band] || band;
        btr.appendChild(bth);
        tbody.appendChild(btr);
      }

      var tr = document.createElement('tr');
      var lab = document.createElement('th');
      lab.setAttribute('scope', 'row');
      lab.textContent = proto.l;
      tr.appendChild(lab);

      cols.forEach(function (c) {
        tr.appendChild(cellNode(rowFor(c, proto.k), rungOrder));
      });

      var allSame = item.keys.every(function (k) { return k === item.keys[0]; });
      if (allSame) tr.classList.add('is-same');

      tbody.appendChild(tr);
    });

    t.appendChild(tbody);
    scroller.appendChild(t);
    return scroller;
  }

  // -------- boot --------

  function boot() { paint(); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* D8 — THE VIEWPORT IS STATE, SO A VIEWPORT CHANGE IS A STATE CHANGE.
   * ⚠️ UNVERIFIED (L-2026-09-04d-5): the emulator changes the viewport without
   * dispatching resize or matchMedia change, so this path could not be tested from
   * a session. It needs a real device rotation or a real window drag. */
  if (window.matchMedia) {
    var mq = window.matchMedia(NARROW_MQ);
    var onViewportChange = function () {
      paint();
      if (modalIsOpen()) openModal();
    };
    if (mq.addEventListener) mq.addEventListener('change', onViewportChange);
    else if (mq.addListener) mq.addListener(onViewportChange);   // Safari < 14
  }

  /* Re-sync ticks when the DOM changes. A CONVENIENCE, not the binding — the
   * delegated listener already works on nodes that did not exist when this ran. */
  if (window.MutationObserver) {
    var pending = false;
    new MutationObserver(function () {
      if (pending) return;
      pending = true;
      window.setTimeout(function () { pending = false; syncBoxes(); }, 60);
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* Another tab changed the tray — keep them agreed. */
  window.addEventListener('storage', function (e) {
    if (e.key === STATE_KEY) paint();
  });

})();
